import React, { useMemo, useState } from 'react';
import uuid from 'short-uuid';
import { Flex } from 'antd';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatCurrency } from '@shared/utils/currency';
import { EstimateBunker, GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import { sortByFuelGrade } from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/utils/helpers';
import EditableTable from '@client/components/EditableTable';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { OnUpdateEstimate } from '../context/hooks/useEstimateHandlers';

type Props = {
	estimate: GetEstimateDetailsResponse;
	onUpdateEstimate: OnUpdateEstimate;
	hasPendingChanges: boolean;
	onSaveChanges: () => void; 
	saving: boolean;

};

const RobsAndPricesModal = ({ 
	estimate,
	onUpdateEstimate,
	onSaveChanges,
	saving,
	hasPendingChanges,
}: Props) => {
	const [tableKey, setTableKey] = useState(0);
	const incrementKey = () => setTableKey((prev) => prev + 1);

	const saveButton = useSaveButton({
		title: 'Save & Close',
		hasPendingChanges: hasPendingChanges,
		loading: saving,
		onSave: onSaveChanges,
	});

	const bunkers = useMemo(() => estimate.commencementBunkers.map((b) => ({
		...b,
		id: uuid.generate(),
	})), [estimate.commencementBunkers]);

	const columns = useMemo(() => {
		return (
			[
				{
					title: 'Fuel grade',
					dataIndex: 'fuelGrade',
					editable: true,
					editingProps: {
						type: 'select',
						inputProps: {
							defaultActiveFirstOption: true,
							options: estimate.vesselFuelGrades.map((fuelGrade) => ({
								label: fuelGrade,
								value: fuelGrade,
							})),
						},
					},
				},
				{
					title: 'Quantity',
					dataIndex: 'quantity',
					editable: true,
					align: 'right',
					editingProps: {
						type: 'number',
						inputProps: {
							addonAfter: 'MT',
						},
					},
					render: (q: number) => `${formatNumber(q, { separateThousands: true }, 3)} MT`,
				},
				{
					dataIndex: 'pricePerTon',
					title: 'Price / MT',
					editable: true,
					editingProps: {
						type: 'number',
					},
					render: (c: number) => {
						const price = c;

						if (c == null || Number.isNaN(c)) {
							return 0;
						}

						return `${formatCurrency(price, estimate.currency)}`;
					},
				}
			]
		);
	}, []);

	const fuelGradeCounts = useMemo(() => (
		bunkers.reduce((acc: Record<string, number>, bunker) => ({
			...acc,
			[bunker.fuelGrade]: (acc[bunker.fuelGrade] ?? 0) + 1,
		}), {})
	), [bunkers]);

	const addRow = (values: EstimateBunker) => {
		onUpdateEstimate({
			commencementBunkers: [...bunkers, {
				...values,
				id: uuid.generate(),
			}]
		});
		incrementKey();
	};

	const onSaveRow = (id: string, values: EstimateBunker) => {
		onUpdateEstimate({
			commencementBunkers: bunkers.map((rob) => rob.id === id ? values : rob),
		});
	};

	const onDelete = (id: string) => {
		onUpdateEstimate({
			commencementBunkers: bunkers.filter((r) => r.id !== id),
		});

	};

	return (
		<Flex gap={10} vertical>
			<EditableTable<EstimateBunker, 'id'>
				key={tableKey}
				dataSource={sortByFuelGrade(bunkers)}
				addNewText="Add entry"
				pagination={false}
				// @ts-ignore
				onSave={onSaveRow}
				emptyText="Empty"
				//@ts-ignore
				columns={columns}
				keyDataIndex="id"
				allowAddNew
				size="small"
				onAddNew={addRow}
				enableDelete={(row) => (fuelGradeCounts[row.fuelGrade] ?? 0) > 1}
				enableEdit
				// @ts-ignore

				onDelete={onDelete}
				iconButtons
				actionsTitle=""
				addButtonInHeader={false}
			/>
			{saveButton}
		</Flex>
	);
};

export default RobsAndPricesModal;
